// Global Instructions Rule Applied!
//
// The collapse chevron shared by every agent surface section — Current State,
// Agent Drafts and Agent Signals (owner, 2026-09-06).
//
// The affordance was built once, inside StatesStrip, and only that section
// could be folded: the two sections beneath it on a Dashboard had no control
// at all, so the only way to shorten a busy LP page was to collapse the one
// section that was already the most compact. This hook is that chevron and its
// persistence lifted out verbatim, so the three sections behave identically.
//
// It deliberately owns the CHEVRON and the STORED POSITION, not the collapsing
// itself. Every section collapses to a COMPACT SUMMARY, never to nothing —
// folding is for reclaiming height, not for hiding what is open — but the
// summary differs: the states strip swaps its cards for a one-line pill row,
// signals for one line per signal (severity · kind · title), drafts for one
// line per draft (status · subject · recipients). A FormSection-level
// `collapsible` prop could express none of those, which is why this is a hook
// rather than a core-component feature.
//
// Each summary row is itself a click target that unfolds the section, so a
// reader who spots the one they want gets to it in a single click.
//
// Persistence is the per-user app-preference blob, not local storage
// (CLAUDE.md): the local store is written first so every section on the
// surface agrees immediately, then the write is fired at the backend and not
// awaited — the MainTabContainer area precedent.
import React, { useCallback, useState } from 'react'
import { Button, Tooltip } from 'antd'
import { DownOutlined, RightOutlined } from '@ant-design/icons'
import { store } from '../../state_manager/store.ts'
import { setUserPreferencesAppData } from '../../state_manager/state_update_actions/global_configs/actions.ts'
import { persistAppPreference } from '../../app_modules/global_configs/controller.ts'
import {
  PREF_APP_AGENT_STATES_COLLAPSED,
  readAgentStatesCollapsed
} from '../../app_modules/global_configs/appPreferences.ts'

export interface SectionCollapse {
  /** Current position. Always false when no collapseKey was given. */
  collapsed: boolean
  /** Sets the position AND persists it. Sections expose it beyond the chevron
   *  (the states strip expands when a pill is clicked). */
  setCollapsed: (next: boolean) => void
  /** Drop into FormSection's `headerLeading`. Null without a collapseKey, so a
   *  caller that has not opted in renders no chevron. */
  chevron: React.ReactNode
}

/**
 * @param collapseKey  Surface key inside the preference blob, e.g.
 *                     'lp_dashboard_signals'. Absent = always expanded, no
 *                     chevron — the opt-out for a section that must not fold.
 * @param defaultCollapsed  Starting position before the user has chosen one on
 *                     this surface. A stored choice always wins.
 * @param labels       Tooltip/aria wording. The states strip says "state
 *                     cards" / "state pills" because collapsing changes the
 *                     shape there; the default says "section" because it
 *                     simply hides the body.
 */
export function useSectionCollapse(
  collapseKey?: string,
  defaultCollapsed: boolean = false,
  labels: { expand: string; collapse: string } = {
    expand: 'Expand this section',
    collapse: 'Collapse this section'
  }
): SectionCollapse {
  const [collapsed, setCollapsedState] = useState<boolean>(() =>
    collapseKey
      ? readAgentStatesCollapsed((store.getState() as any).globalConfigs, collapseKey, defaultCollapsed)
      : false
  )

  const setCollapsed = useCallback(
    (next: boolean) => {
      setCollapsedState(next)
      if (!collapseKey) return
      const current = ((store.getState() as any).globalConfigs?.user_preferences_app?.data?.[
        PREF_APP_AGENT_STATES_COLLAPSED
      ] ?? {}) as Record<string, boolean>
      const merged = { [PREF_APP_AGENT_STATES_COLLAPSED]: { ...current, [collapseKey]: next } }
      // Local store first so every section on this surface agrees immediately;
      // persistence is fire-and-forget (the MainTabContainer area precedent).
      store.dispatch(setUserPreferencesAppData(merged))
      void persistAppPreference(merged)
    },
    [collapseKey]
  )

  // Leading, before the title dot (the mockup's placement) — a trailing
  // chevron gets lost next to the header's tags.
  const chevron = collapseKey ? (
    <Tooltip title={collapsed ? labels.expand : labels.collapse} mouseEnterDelay={0.4}>
      <Button
        type='text'
        size='small'
        className='px-1 -ml-2'
        aria-label={collapsed ? labels.expand : labels.collapse}
        icon={collapsed ? <RightOutlined style={{ fontSize: 10 }} /> : <DownOutlined style={{ fontSize: 10 }} />}
        onClick={() => setCollapsed(!collapsed)}
      />
    </Tooltip>
  ) : null

  return { collapsed, setCollapsed, chevron }
}
